import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { Region, RegionStatus } from '../types/fair.types';

//Interface for users who are admins in a region
interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  regionId: string;
}

//Interface for updating regions
interface RegionUpdate {
  name?: string;
  location?: string;
  state?: string;
  status?: string;
  maxAmountOfFairs?: number;
  schools?: string[];
  regionDirectorId?: string;
  isPublic?: boolean;
  parentRegionId?: string | null;
}

//Function to create a new region
export const createRegion = async (
  name: string,
  location: string,
  regionDirectorId: string,
  state: string,
  status: RegionStatus = 'pending',
  maxAmountOfFairs: number = 5,
  isPublic: boolean = true,
  parentRegionId?: string | null
): Promise<string> => {
  //Create the region data object
  const regionData = {
    name,
    location,
    fairIds: [],
    regionDirectorId,
    state,
    status,
    maxAmountOfFairs,
    schools: [],
    isPublic,
    parentRegionId: parentRegionId || null,
  };
  try {
    //Add the region data to the 'regions' collection in Firestore
    const regionRef = await addDoc(collection(db, 'regions'), regionData);
    const region = {
      id: regionRef.id,
      ...regionData,
    };
    //return region ID if needed
    return region.id;
  } catch (error) {
    console.error('Error creating region:', error);
    throw error;
  }
};

//Function to update a region
export const updateRegion = async (
  regionId: string,
  updatedFields: RegionUpdate
): Promise<void> => {
  try {
    console.log('Updating region:', updatedFields.parentRegionId);
    //Find the document reference for the region to be updated in the 'regions' collection
    const regionRef = doc(db, 'regions', regionId);
    //Update the document with the new data
    await updateDoc(regionRef, { ...updatedFields });
    console.log(`Region with ID ${regionId} updated successfully.`);
  } catch (error) {
    console.error(`Error updating region with ID ${regionId}:`, error);
    throw error;
  }
};

//Function to get all regions
export const getRegions = async (): Promise<Region[]> => {
  try {
    //Get all documents from the 'regions' collection
    const regionsSnapshot = await getDocs(collection(db, 'regions'));
    //Map the documents to an array of regions and return it
    return regionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Region[];
  } catch (error) {
    console.error('Error fetching regions:', error);
    throw error;
  }
};

//Function to get a single region by its ID
export const getRegionById = async (
  regionId: string
): Promise<Region | undefined> => {
  try {
    //Get all regions from the 'regions' collection
    const regionsSnapshot = await getDocs(collection(db, 'regions'));
    //Map the documents to an array of regions
    const allRegions = regionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Region[];

    //Find the region with the matching ID
    const region = allRegions.find((region) => region.id === regionId);
    //return the region if found, otherwise return undefined
    return region;
  } catch (error) {
    console.error('Error fetching region by ID:', error);
    throw error;
  }
};

//Function to get all 'active' regions
export const getActiveRegions = async (): Promise<Region[]> => {
  try {
    //Get all regions from the 'regions' collection
    const regionsSnapshot = await getDocs(collection(db, 'regions'));
    //Map the documents to an array of regions
    const allRegions = regionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Region[];

    //Filter the regions to only include those with status "active"
    const activeRegions = allRegions.filter(
      (region) => region.status === 'active'
    );

    //return the active regions
    return activeRegions;
  } catch (error) {
    console.error('Error fetching active regions:', error);
    throw error;
  }
};

//Function to get all active and public regions
export const getActivePublicRegions = async (): Promise<Region[]> => {
  try {
    //Get all regions from the 'regions' collection
    const regionsSnapshot = await getDocs(collection(db, 'regions'));
    //Map the documents to an array of regions
    const allRegions = regionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Region[];

    //Filter the regions to only include those with status "active" and isPublic true
    const activePublicRegions = allRegions.filter(
      (region) => region.status === 'active' && region.isPublic === true
    );

    //return the active regions
    return activePublicRegions;
  } catch (error) {
    console.error('Error fetching active and public regions:', error);
    throw error;
  }
};

//Function to delete a region by its ID
export const deleteRegion = async (regionId: string): Promise<void> => {
  try {
    //Find the document reference for the region to be deleted in the 'regions' collection by it's ID
    const regionRef = doc(db, 'regions', regionId);
    //Delete the document from the database
    await deleteDoc(regionRef);
    console.log(`Region with ID ${regionId} deleted successfully.`);
  } catch (error) {
    console.error(`Error deleting region with ID ${regionId}:`, error);
    throw error;
  }
};

//Function to get all admins in a specific region by its ID
export const getAdminsInRegion = async (
  regionId: string
): Promise<AdminUser[]> => {
  try {
    //Get a reference to all users in the 'users' collection
    const usersRef = collection(db, 'users');
    //Create a query to find users who are admins and belong to the specified region
    const q = query(
      usersRef,
      where('userType', '==', 'admin'),
      where('regionId', '==', regionId)
    );

    //Execute the query and get the results
    const querySnapshot = await getDocs(q);
    //Map the results to an array of AdminUser objects
    const adminsInRegion: AdminUser[] = [];

    //Create an array of AdminUser objects from the query results
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      adminsInRegion.push({
        id: doc.id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        userType: userData.userType || '',
        regionId: userData.regionId || '',
      } as AdminUser);
    });

    //return the array of AdminUser objects
    return adminsInRegion;
  } catch (error) {
    console.error(`Error fetching admins in region ${regionId}:`, error);
    throw error;
  }
};

export const getMaxAmountOfFairs = async (
  regionId: string
): Promise<number> => {
  try {
    //Get the region by ID
    const region = await getRegionById(regionId);
    //If the region is found, return the maxAmountOfFairs, otherwise return 0
    return region ? region.maxAmountOfFairs : 0;
  } catch (error) {
    console.error(
      `Error fetching max amount of fairs for region ${regionId}:`,
      error
    );
    throw error;
  }
};

//Function to get all admin users for region director selection
export const getAllAdminUsers = async (): Promise<AdminUser[]> => {
  try {
    //Get a reference to all users in the 'users' collection
    const usersRef = collection(db, 'users');
    //Create a query to find users who are admins
    const q = query(usersRef, where('userType', '==', 'admin'));

    //Execute the query and get the results
    const querySnapshot = await getDocs(q);
    //Map the results to an array of AdminUser objects
    const adminUsers: AdminUser[] = [];

    //Create an array of AdminUser objects from the query results
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      adminUsers.push({
        id: doc.id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        userType: userData.userType || '',
        regionId: userData.regionId || '',
      } as AdminUser);
    });

    //return the array of AdminUser objects
    return adminUsers;
  } catch (error) {
    console.error('Error fetching all admin users:', error);
    throw error;
  }
};

export const getRegionStatus = async (
  regionId: string
): Promise<RegionStatus | undefined> => {
  try {
    //Get the region by ID
    const region = await getRegionById(regionId);
    //If the region is found, return the status, otherwise return undefined
    return region ? region.status : undefined;
  } catch (error) {
    console.error(`Error fetching status for region ${regionId}:`, error);
    throw error;
  }
};

//Function to get all regions to choose from when selecting a parent region
export const getPossibleParentRegions = async (
  regionId: string
): Promise<Region[]> => {
  try {
    // Get all regions from the 'regions' collection
    const regionsRef = collection(db, 'regions');

    // Use only one inequality filter along with the equality filter
    const q = query(regionsRef, where('status', '==', 'active'));

    const regionsSnapshot = await getDocs(q);

    // Filter the results in memory after fetching them
    const possibleRegions = regionsSnapshot.docs
      .map((doc) => {
        const data = doc.data() as Omit<Region, 'id'>;
        return {
          id: doc.id,
          ...data,
        };
      })
      .filter(
        (region) => region.parentRegionId !== regionId && region.id !== regionId
      );

    // Return the possible regions
    console.log('Possible parent regions:', possibleRegions);
    return possibleRegions;
  } catch (error) {
    console.error('Error fetching possible parent regions:', error);
    throw error;
  }
};
